import React, { useState, useEffect, useMemo } from 'react';
import { Settings, Edit, Save, X, Plus, Trash2, Image, Link, Palette, Eye, EyeOff, Sparkles, Calendar, Layers, ArrowRight, ExternalLink, Upload, Smartphone, Check, AlertCircle } from 'lucide-react';
import api from '../../helpers/api_helper';
import { getAppIdFromAccessToken, getRoleFromAccessToken } from '../../helpers/jwt';

interface Banner {
  id: number;
  title: string;
  image_url: string;
  description: string;
}

interface Button {
  id: string;
  url: string;
  name: string;
  theme: string;
}

interface BannerConfig {
  total_banners: number;
  animation_type: string;
  animation_duration: number;
}

interface ButtonConfig {
  button_theme: string;
  number_of_buttons: number;
}

interface HomepageConfig {
  id: string;
  app_id: string;
  config_json: {
    banners: Banner[];
    buttons: Button[];
    banner_config: BannerConfig;
    button_config: ButtonConfig;
  };
  updated_at: string;
}

interface App {
  id: string;
  app_name: string;
  version: string;
  created_at: string;
  updated_at: string;
}

const AppConfigurations = () => {
  const [configurations, setConfigurations] = useState<HomepageConfig[]>([]);
  const [apps, setApps] = useState<App[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingConfig, setEditingConfig] = useState<HomepageConfig | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [activeTab, setActiveTab] = useState<'banners' | 'buttons' | 'preview'>('banners');
  const [selectedFiles, setSelectedFiles] = useState<{ [bannerId: number]: File | null }>({});
  const [uploading, setUploading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showCreateConfig, setShowCreateConfig] = useState(false);
  const [newConfigAppId, setNewConfigAppId] = useState('');
  const [fileErrors, setFileErrors] = useState<{ [bannerId: number]: string | null }>({});

  // Get Admin/Sub Admin's app_id and role
  const adminAppId = getAppIdFromAccessToken() || localStorage.getItem('app_id') || localStorage.getItem('selectedAppId');
  const userRole = getRoleFromAccessToken() || '';
  const roleLower = userRole.toLowerCase();
  const isAdmin = roleLower === 'admin';
  const isSubAdmin = roleLower === 'sub admin' || roleLower === 'subadmin' || roleLower === 'sub_admin' || (roleLower.includes('sub') && roleLower.includes('admin') && roleLower !== 'superadmin');

  // Filter configurations to show only Admin/Sub Admin's app_id for Admin/Sub Admin roles
  const filteredConfigurations = useMemo(() => {
    if ((isAdmin || isSubAdmin) && adminAppId) {
      return configurations.filter(config => config.app_id === adminAppId);
    }
    return configurations;
  }, [configurations, isAdmin, isSubAdmin, adminAppId]);

  useEffect(() => {
    fetchConfigurations();
    fetchApps();
  }, []);

  const fetchConfigurations = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/homepage/');
      console.log('Fetch Configurations API Response:', response.data);
      
      // Handle different response structures
      let configs = response.data;
      if (response.data && response.data.data && Array.isArray(response.data.data)) {
        configs = response.data.data;
      } else if (response.data && response.data.results && Array.isArray(response.data.results)) {
        configs = response.data.results;
      } else if (Array.isArray(response.data)) {
        configs = response.data;
      }
      
      if (configs && Array.isArray(configs)) {
        const validatedConfigs = configs.map((config: any) => {
          if (!config.config_json) {
            return {
              ...config,
              config_json: {
                banners: [],
                buttons: [],
                banner_config: { total_banners: 5, animation_type: 'slide', animation_duration: 2000 },
                button_config: { button_theme: 'rounded', number_of_buttons: 10 }
              }
            };
          }
          return config;
        });
        setConfigurations(validatedConfigs);
      } else {
        setError('Invalid response format from API');
      }
    } catch (error: any) {
      console.error('Error fetching configurations:', error);
      setError(error.response?.data?.detail || error.message || 'Failed to fetch configurations');
    } finally {
      setLoading(false);
    }
  };

  const fetchApps = async () => {
    try {
      const response = await api.get('/apps/');
      if (response.data && Array.isArray(response.data)) {
        setApps(response.data);
      }
    } catch (error: any) {
      console.error('Error fetching apps:', error);
    }
  };

  const handleEdit = (config: HomepageConfig) => {
    setEditingConfig({ ...config });
    setIsEditing(true);
    setActiveTab('banners');
  };

  const handleSave = async () => {
    if (!editingConfig) return;
    try {
      const response = await api.patch(`/homepage/${editingConfig.id}/`, {
        config_json: editingConfig.config_json,
      });
      
      // Handle different response structures and preserve app_id
      let updatedConfig = response.data;
      if (response.data && response.data.data) {
        updatedConfig = response.data.data;
      } else if (Array.isArray(response.data) && response.data.length > 0) {
        updatedConfig = response.data[0];
      }
      
      // Preserve app_id from original config if not in response
      if (!updatedConfig.app_id && editingConfig.app_id) {
        updatedConfig = { ...updatedConfig, app_id: editingConfig.app_id };
      }
      
      setConfigurations(prev => prev.map(config => 
        config.id === editingConfig.id ? updatedConfig : config
      ));
      setIsEditing(false);
      setEditingConfig(null);
      setSuccessMessage('Configuration updated successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error: any) {
      console.error('Error updating configuration:', error);
      setError(error.response?.data?.detail || error.message || 'Failed to update configuration');
    }
  };

  const handleFileSelect = (bannerId: number, file: File | null) => {
    if (!file) {
      setSelectedFiles(prev => ({ ...prev, [bannerId]: null }));
      setFileErrors(prev => ({ ...prev, [bannerId]: null }));
      return;
    }

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setFileErrors(prev => ({ ...prev, [bannerId]: 'Invalid file type. Only PNG, JPEG, JPG, and WebP are allowed.' }));
      setSelectedFiles(prev => ({ ...prev, [bannerId]: null }));
      return;
    }

    // Validate file size (5MB max)
    const maxSize = 10 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      setFileErrors(prev => ({ ...prev, [bannerId]: 'File too large. Maximum size is 10MB.' }));
      setSelectedFiles(prev => ({ ...prev, [bannerId]: null }));
      return;
    }

    // File is valid
    setSelectedFiles(prev => ({ ...prev, [bannerId]: file }));
  };

  const handleBannerUpload = async (bannerId: number, title: string, description: string) => {
    const selectedFile = selectedFiles[bannerId];
    if (!selectedFile || !editingConfig) return;
    
    try {
      setUploading(true);
      setError(null);
      
      // Step 1: Get Pre-signed URL
      const fileExtension = selectedFile.name.substring(selectedFile.name.lastIndexOf('.'));
      const contentType = selectedFile.type || 'image/jpeg';
      const maxSize = 5 * 1024 * 1024; // 5MB
      
      const presignedResponse = await api.post('/presigned-url/', {
        file_type: 'banner',
        file_extension: fileExtension,
        content_type: contentType,
        max_size: maxSize
      });

      const { presigned_url, presigned_fields, s3_key } = presignedResponse.data;
      
      if (!presigned_url || !presigned_fields || !s3_key) {
        throw new Error('Invalid response from pre-signed URL endpoint');
      }

      // Step 2: Upload to S3
      const s3FormData = new FormData();
      Object.keys(presigned_fields).forEach(key => {
        s3FormData.append(key, presigned_fields[key]);
      });
      s3FormData.append('file', selectedFile);

      const s3UploadResponse = await fetch(presigned_url, {
        method: 'POST',
        body: s3FormData
      });

      if (!s3UploadResponse.ok) {
        throw new Error(`S3 upload failed: ${s3UploadResponse.status} ${s3UploadResponse.statusText}`);
      }

      // Step 3: Update Homepage with S3 Key
      const updatePayload: any = {
        s3_key: s3_key
      };

      // Optional fields - only include if they have values
      if (bannerId) {
        updatePayload.banner_id = bannerId;
      }
      
      if (title && title.trim()) {
        updatePayload.title = title.trim();
      }
      
      if (description && description.trim()) {
        updatePayload.description = description.trim();
      }

      const response = await api.post(`/homepage/${editingConfig.id}/upload-banner/`, updatePayload);

      // Handle response - check for success flag or data
      if (response.data) {
        let updatedConfig: HomepageConfig;
        
        if (response.data.success && response.data.data) {
          // Response has success flag and data
          updatedConfig = response.data.data;
        } else if (response.data.data) {
          // Response has data directly
          updatedConfig = response.data.data;
        } else {
          // Response is the config itself
          updatedConfig = response.data;
        }
        
        // Preserve app_id from original config if not in response
        if (!updatedConfig.app_id && editingConfig.app_id) {
          updatedConfig = { ...updatedConfig, app_id: editingConfig.app_id };
        }
        
        // Update configurations state
        setConfigurations(prev => prev.map(config => 
          config.id === editingConfig.id ? updatedConfig : config
        ));
        
        // Update editing config to reflect changes
        setEditingConfig(updatedConfig);
        
        // Clear selected file and errors
        setSelectedFiles(prev => ({ ...prev, [bannerId]: null }));
        setFileErrors(prev => ({ ...prev, [bannerId]: null }));
        setError(null);
        setSuccessMessage('Banner uploaded successfully!');
        setTimeout(() => setSuccessMessage(null), 3000);
      }
    } catch (error: any) {
      console.error('Error uploading banner:', error);
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.detail || 
                          error.response?.data?.message ||
                          error.message || 
                          'Failed to upload banner';
      setError(errorMessage);
      setSuccessMessage(null);
    } finally {
      setUploading(false);
    }
  };

  const handleCreateConfig = async () => {
    if (!newConfigAppId) return;
    
    try {
      const response = await api.post('/homepage/', {
        app_id: newConfigAppId,
        config_json: {
          banners: [],
          buttons: [],
          banner_config: { total_banners: 5, animation_type: 'slide', animation_duration: 2000 },
          button_config: { button_theme: 'rounded', number_of_buttons: 10 }
        }
      });
      
      console.log('Create Config API Response:', response.data);
      
      // Handle the response structure - extract data from response.data.data
      const newConfig = response.data.data || response.data;
      setConfigurations(prev => [...prev, newConfig]);
      setShowCreateConfig(false);
      setNewConfigAppId('');
    } catch (error: any) {
      console.error('Error creating configuration:', error);
      setError(error.response?.data?.detail || error.message || 'Failed to create configuration');
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditingConfig(null);
    setError(null);
  };

  const addBanner = () => {
    if (!editingConfig) return;
    const newBanner: Banner = {
      id: Date.now(),
      title: 'New Banner',
      image_url: '',
      description: 'New banner description'
    };
    setEditingConfig({
      ...editingConfig,
      config_json: {
        ...editingConfig.config_json,
        banners: [...editingConfig.config_json.banners, newBanner]
      }
    });
  };

  const removeBanner = (index: number) => {
    if (!editingConfig) return;
    const newBanners = editingConfig.config_json.banners.filter((_, i) => i !== index);
    setEditingConfig({
      ...editingConfig,
      config_json: {
        ...editingConfig.config_json,
        banners: newBanners
      }
    });
  };

  const updateBanner = (index: number, field: keyof Banner, value: string) => {
    if (!editingConfig) return;
    const newBanners = [...editingConfig.config_json.banners];
    newBanners[index] = { ...newBanners[index], [field]: value };
    setEditingConfig({
      ...editingConfig,
      config_json: {
        ...editingConfig.config_json,
        banners: newBanners
      }
    });
  };

  const addButton = () => {
    if (!editingConfig) return;
    const newButton: Button = {
      id: `btn_${Date.now()}`,
      url: '',
      name: 'New Button',
      theme: 'primary'
    };
    setEditingConfig({
      ...editingConfig,
      config_json: {
        ...editingConfig.config_json,
        buttons: [...editingConfig.config_json.buttons, newButton]
      }
    });
  };

  const removeButton = (index: number) => {
    if (!editingConfig) return;
    const newButtons = editingConfig.config_json.buttons.filter((_, i) => i !== index);
    setEditingConfig({
      ...editingConfig,
      config_json: {
        ...editingConfig.config_json,
        buttons: newButtons
      }
    });
  };

  const updateButton = (index: number, field: keyof Button, value: string) => {
    if (!editingConfig) return;
    const newButtons = [...editingConfig.config_json.buttons];
    newButtons[index] = { ...newButtons[index], [field]: value };
    setEditingConfig({
      ...editingConfig,
      config_json: {
        ...editingConfig.config_json,
        buttons: newButtons
      }
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getThemeColors = (theme: string) => {
    const colors: { [key: string]: string } = {
      primary: 'bg-orange-500 hover:bg-orange-600',
      secondary: 'bg-gray-500 hover:bg-gray-600',
      success: 'bg-green-500 hover:bg-green-600',
      danger: 'bg-red-500 hover:bg-red-600',
      warning: 'bg-yellow-500 hover:bg-yellow-600',
      info: 'bg-cyan-500 hover:bg-cyan-600',
      light: 'bg-gray-100 hover:bg-gray-200 text-gray-800',
      dark: 'bg-gray-800 hover:bg-gray-900'
    };
    return colors[theme] || colors.primary;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">Error: {error}</div>
          <button
            onClick={fetchConfigurations}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <Settings className="w-8 h-8 text-blue-500" />
          App Configurations
        </h1>
        <p className="text-gray-600 mt-2">Manage homepage banners, buttons, and app settings</p>
          </div>
          {/* Hide Create button for Admin and Sub Admin roles */}
          {!isAdmin && !isSubAdmin && (
            <button
              onClick={() => setShowCreateConfig(true)}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              Create New Configuration
            </button>
          )}
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg flex items-center gap-2">
          <Check className="w-5 h-5" />
          {successMessage}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          {error}
        </div>
      )}

      {filteredConfigurations.length === 0 ? (
        <div className="text-center py-12">
          <Layers className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No configurations found</h3>
          <p className="text-gray-500">
            {(isAdmin || isSubAdmin) ? 'No configuration found for your app.' : 'Create your first app configuration to get started.'}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {filteredConfigurations.map((config) => (
            <div key={config.id} className="bg-white rounded-lg shadow-md border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    {!isAdmin && !isSubAdmin && (
                      <h3 className="text-lg font-semibold text-gray-900">
                        App ID: {config.app_id}
                      </h3>
                    )}
                    <p className="text-sm text-gray-500">
                      Last updated: {formatDate(config.updated_at)}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {!isEditing && (
                      <button
                        onClick={() => handleEdit(config)}
                        className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors"
                      >
                        <Edit className="w-4 h-4" />
                        Edit
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {isEditing && editingConfig?.id === config.id ? (
                <div className="p-6">
                  <div className="mb-6">
                    <div className="flex border-b border-gray-200">
                      <button
                        onClick={() => setActiveTab('banners')}
                        className={`px-4 py-2 font-medium transition-colors ${
                          activeTab === 'banners'
                            ? 'text-blue-600 border-b-2 border-blue-600'
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        Banners ({editingConfig.config_json?.banners?.length || 0})
                      </button>
                      <button
                        onClick={() => setActiveTab('buttons')}
                        className={`px-4 py-2 font-medium transition-colors ${
                          activeTab === 'buttons'
                            ? 'text-blue-600 border-b-2 border-blue-600'
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        Buttons ({editingConfig.config_json?.buttons?.length || 0})
                      </button>
                      <button
                        onClick={() => setActiveTab('preview')}
                        className={`px-4 py-2 font-medium transition-colors ${
                          activeTab === 'preview'
                            ? 'text-blue-600 border-b-2 border-blue-600'
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        Preview
                      </button>
                    </div>
                  </div>

                  {activeTab === 'banners' && (
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-lg font-medium text-gray-900">Banner Management</h4>
                        <button
                          onClick={addBanner}
                          className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-3 py-2 rounded-lg transition-colors"
                        >
                          <Plus className="w-4 h-4" />
                          Add Banner
                        </button>
                      </div>
                      
                      <div className="space-y-4">
                        {(editingConfig.config_json?.banners || []).map((banner, index) => (
                          <div key={banner.id} className="border border-gray-200 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-3">
                              <h5 className="font-medium text-gray-900">Banner {index + 1}</h5>
                              <button
                                onClick={() => removeBanner(index)}
                                className="text-red-500 hover:text-red-700 transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                            
                            {/* Banner Image Upload Section */}
                            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                              <h6 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                                <Upload className="w-4 h-4" />
                                Upload Banner Image
                              </h6>
                              <div className="space-y-3">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Banner Image <span className="text-red-500">*</span>
                                  </label>
                                  <input
                                    type="file"
                                    accept="image/png,image/jpeg,image/jpg,image/webp"
                                    onChange={(e) => handleFileSelect(banner.id, e.target.files?.[0] || null)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  />
                                  {fileErrors[banner.id] && (
                                    <p className="text-sm text-red-600 mt-1 flex items-center gap-1">
                                      <AlertCircle className="w-4 h-4" />
                                      {fileErrors[banner.id]}
                                    </p>
                                  )}
                                  {selectedFiles[banner.id] && !fileErrors[banner.id] && (
                                    <div className="mt-2">
                                      <p className="text-sm text-green-600 flex items-center gap-1 mb-2">
                                        <Check className="w-4 h-4" />
                                        Selected: {selectedFiles[banner.id]?.name} ({(selectedFiles[banner.id]!.size / 1024 / 1024).toFixed(2)} MB)
                                      </p>
                                      <img 
                                        src={URL.createObjectURL(selectedFiles[banner.id]!)} 
                                        alt="Preview" 
                                        className="max-w-full h-32 object-cover rounded-lg border border-gray-200"
                                      />
                                    </div>
                                  )}
                                  <p className="text-xs text-gray-500 mt-1">
                                    Supported formats: PNG, JPEG, JPG, WebP (Max 5MB)
                                  </p>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Title
                                </label>
                                <input
                                  type="text"
                                  value={banner.title}
                                  onChange={(e) => updateBanner(index, 'title', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                      placeholder="Enter banner title"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Description
                                </label>
                                <input
                                      type="text"
                                      value={banner.description}
                                      onChange={(e) => updateBanner(index, 'description', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                      placeholder="Enter banner description"
                                    />
                                  </div>
                                </div>
                                <button
                                  onClick={() => handleBannerUpload(banner.id, banner.title, banner.description)}
                                  disabled={!selectedFiles[banner.id] || uploading}
                                  className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg transition-colors"
                                >
                                  {uploading ? (
                                    <>
                                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                      Uploading...
                                    </>
                                  ) : (
                                    <>
                                      <Upload className="w-4 h-4" />
                                      Upload Banner
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>

                            {/* Current Banner Display */}
                            {banner.image_url && (
                              <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Current Banner
                                </label>
                                <div className="border border-gray-200 rounded-lg p-3">
                                  <img
                                    src={banner.image_url}
                                    alt={banner.title}
                                    className="w-full h-32 object-cover rounded-lg"
                                    onError={(e) => {
                                      e.currentTarget.style.display = 'none';
                                    }}
                                  />
                                  <p className="text-xs text-gray-500 mt-2 break-all">{banner.image_url}</p>
                                </div>
                              </div>
                            )}

                            {/* Manual URL Input (Fallback) */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Or Enter Image URL
                              </label>
                              <input
                                type="url"
                                value={banner.image_url}
                                onChange={(e) => updateBanner(index, 'image_url', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="https://example.com/image.png"
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {activeTab === 'buttons' && (
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-lg font-medium text-gray-900">Button Management</h4>
                        <button
                          onClick={addButton}
                          className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-3 py-2 rounded-lg transition-colors"
                        >
                          <Plus className="w-4 h-4" />
                          Add Button
                        </button>
                      </div>
                      
                      <div className="space-y-4">
                        {(editingConfig.config_json?.buttons || []).map((button, index) => (
                          <div key={button.id} className="border border-gray-200 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-3">
                              <h5 className="font-medium text-gray-900">Button {index + 1}</h5>
                              <button
                                onClick={() => removeButton(index)}
                                className="text-red-500 hover:text-red-700 transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Name
                                </label>
                                <input
                                  type="text"
                                  value={button.name}
                                  onChange={(e) => updateButton(index, 'name', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  URL
                                </label>
                                <input
                                  type="url"
                                  value={button.url}
                                  onChange={(e) => updateButton(index, 'url', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Theme
                                </label>
                                <select
                                  value={button.theme}
                                  onChange={(e) => updateButton(index, 'theme', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                  <option value="primary">Primary</option>
                                  <option value="secondary">Secondary</option>
                                  <option value="success">Success</option>
                                  <option value="danger">Danger</option>
                                  <option value="warning">Warning</option>
                                  <option value="info">Info</option>
                                  <option value="light">Light</option>
                                  <option value="dark">Dark</option>
                                </select>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {activeTab === 'preview' && (
                    <div>
                      <h4 className="text-lg font-medium text-gray-900 mb-4">Preview</h4>
                      <div className="bg-gray-50 rounded-lg p-6">
                        <div className="mb-6">
                          <h5 className="font-medium text-gray-900 mb-3">Banners</h5>
                          <div className="space-y-3">
                            {(editingConfig.config_json?.banners || []).map((banner, index) => (
                              <div key={banner.id} className="bg-white p-4 rounded-lg border">
                                <div className="flex items-center gap-3">
                                  <Image className="w-5 h-5 text-gray-400" />
                                  <div className="flex-1">
                                    <h6 className="font-medium text-gray-900">{banner.title}</h6>
                                    <p className="text-sm text-gray-600">{banner.description}</p>
                                    {banner.image_url && (
                                      <p className="text-xs text-blue-500 mt-1">{banner.image_url}</p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-gray-900 mb-3">Buttons</h5>
                          <div className="flex flex-wrap gap-2">
                            {(editingConfig.config_json?.buttons || []).map((button) => (
                              <button
                                key={button.id}
                                className={`px-4 py-2 rounded-lg text-white text-sm ${getThemeColors(button.theme)}`}
                              >
                                {button.name}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex gap-3 mt-6 pt-6 border-t border-gray-200">
                    <button
                      onClick={handleSave}
                      className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-6 py-2 rounded-lg transition-colors"
                    >
                      <Save className="w-4 h-4" />
                      Save Changes
                    </button>
                    <button
                      onClick={handleCancel}
                      className="flex items-center gap-2 bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded-lg transition-colors"
                    >
                      <X className="w-4 h-4" />
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="text-lg font-medium text-gray-900 mb-3 flex items-center gap-2">
                        <Image className="w-5 h-5 text-blue-500" />
                        Banners ({config.config_json?.banners?.length || 0})
                      </h4>
                      <div className="space-y-2">
                        {(config.config_json?.banners || []).map((banner, index) => (
                          <div key={banner.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                            <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                              <span className="text-sm font-medium text-blue-600">{index + 1}</span>
                            </div>
                            <div className="flex-1">
                              <h6 className="font-medium text-gray-900">{banner.title}</h6>
                              <p className="text-sm text-gray-600">{banner.description}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="text-lg font-medium text-gray-900 mb-3 flex items-center gap-2">
                        <Link className="w-5 h-5 text-green-500" />
                        Buttons ({config.config_json?.buttons?.length || 0})
                      </h4>
                      <div className="space-y-2">
                        {(config.config_json?.buttons || []).map((button, index) => (
                          <div key={button.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                              <span className="text-sm font-medium text-green-600">{index + 1}</span>
                            </div>
                            <div className="flex-1">
                              <h6 className="font-medium text-gray-900">{button.name}</h6>
                              <p className="text-sm text-gray-600">{button.url}</p>
                            </div>
                            <div className={`px-2 py-1 rounded text-xs text-white ${getThemeColors(button.theme)}`}>
                              {button.theme}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create New Configuration Modal */}
      {showCreateConfig && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Smartphone className="w-5 h-5 text-blue-600" />
                  Create New Configuration
                </h3>
                <button
                  onClick={() => setShowCreateConfig(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select App *
                </label>
                <select
                  value={newConfigAppId}
                  onChange={(e) => setNewConfigAppId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Choose an app...</option>
                  {apps.map((app) => (
                    <option key={app.id} value={app.id}>
                      {app.app_name} (v{app.version})
                    </option>
                  ))}
                </select>
              </div>
              <div className="text-sm text-gray-600">
                <p>This will create a new homepage configuration for the selected app with default settings.</p>
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50 rounded-b-xl">
              <div className="flex gap-3">
                <button
                  onClick={handleCreateConfig}
                  disabled={!newConfigAppId}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white py-2 px-4 rounded-lg transition-colors"
                >
                  Create Configuration
                </button>
                <button
                  onClick={() => setShowCreateConfig(false)}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 py-2 px-4 rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppConfigurations;
